// NEW_FILE_JUSTIFIED: New component for rendering dynamic insurance policy fields — no existing equivalent
import { useRef, useCallback, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ are two levels deep, requiring three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: components in src/components/insurance/ are two levels deep, requiring three levels up to reach convex/_generated/
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Upload, X, FileText, Building2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IDynamicFieldInputProps, IInsuranceProvider } from '@/@types/insurance';

export function DynamicFieldInput({
  field,
  value,
  storageIds,
  onChange,
  onFileUpload,
  onFileRemove,
  orgId,
  disabled,
}: IDynamicFieldInputProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileNames, setFileNames] = useState<Map<string, string>>(new Map());
  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const providers = useQuery(api['insurance/providers'].list, orgId ? { orgId } : 'skip');

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      try {
        const uploadUrl = await createUploadUrl({});
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is typed as { storageId: Id<'_storage'> }
        onFileUpload(storageId);
      } catch (err) {
        console.error('File upload failed:', err);
      }
      e.target.value = '';
    },
    [createUploadUrl, onFileUpload]
  );

  const handleClaimsFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const fileName = file.name;
      try {
        const uploadUrl = await createUploadUrl({});
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is typed as { storageId: Id<'_storage'> }
        setFileNames(prev => new Map(prev).set(storageId, fileName));
        onFileUpload(storageId);
      } catch (err) {
        console.error('File upload failed:', err);
      }
      e.target.value = '';
    },
    [createUploadUrl, onFileUpload]
  );

  const handleClaimsFileRemove = useCallback(
    (storageId: Id<'_storage'>) => {
      setFileNames(prev => {
        const next = new Map(prev);
        next.delete(storageId);
        return next;
      });
      onFileRemove(storageId);
    },
    [onFileRemove]
  );

  if (field.fieldType === 'boolean') {
    return (
      <div className="flex items-center gap-3 !h-10 rounded-md border border-input bg-transparent dark:bg-input/30 px-3">
        <Checkbox
          id={`field-${field.label}`}
          checked={value === 'true'}
          onCheckedChange={checked => onChange(checked ? 'true' : 'false')}
          disabled={disabled}
        />
        <label
          htmlFor={`field-${field.label}`}
          className={cn(
            'text-sm font-medium text-foreground',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {value === 'true' ? 'Yes' : 'No'}
        </label>
      </div>
    );
  }

  if (field.fieldType === 'select') {
    return (
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full !h-10">
          <SelectValue placeholder={`Select ${field.label}`} />
        </SelectTrigger>
        <SelectContent>
          {(field.options ?? []).map(option => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.fieldType === 'provider') {
    const providerList = providers ?? [];
    if (providerList.length === 0) {
      return (
        <Button
          type="button"
          variant="outline"
          onClick={() => window.open('/insurance/providers/new', '_blank')}
          disabled={disabled}
          className="self-start"
        >
          <Building2 className="w-4 h-4 mr-1.5" />
          Create Provider
        </Button>
      );
    }
    return (
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full !h-10">
          <SelectValue placeholder="Select provider" />
        </SelectTrigger>
        <SelectContent>
          {providerList.map((p: IInsuranceProvider) => (
            <SelectItem key={p._id} value={p._id}>
              {p.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (field.fieldType === 'claims') {
    return (
      <div className="flex flex-col gap-3">
        <Input
          type="number"
          min="0"
          value={value === '' ? '' : String(Number(value))}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          placeholder="Number of claims"
          className="!h-10"
        />
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground">Upload claim documents (max 10)</p>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            disabled={disabled}
            onChange={handleClaimsFileUpload}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || storageIds.length >= 10}
            className="w-full flex items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-4 px-3.5 hover:bg-muted/50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-[18px] h-[18px] text-muted-foreground" />
            <span className="text-[13px] font-medium text-muted-foreground">Add document</span>
          </button>
          {storageIds.length > 0 && (
            <div className="flex flex-col gap-2">
              {storageIds.map((storageId, idx) => (
                <div
                  key={storageId}
                  className="flex items-center gap-2 px-3 py-2 bg-muted rounded-md text-sm text-foreground"
                >
                  <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">
                    {fileNames.get(storageId) ?? `Document ${idx + 1}`}
                  </span>
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleClaimsFileRemove(storageId)}
                      className="ml-auto text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (field.fieldType === 'file') {
    return (
      <div className="flex flex-col gap-2">
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          disabled={disabled}
          onChange={handleFileUpload}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="self-start"
        >
          <Upload className="w-4 h-4 mr-1.5" />
          Upload File
        </Button>
        {storageIds.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {storageIds.map(storageId => (
              <div
                key={storageId}
                className="flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs text-muted-foreground"
              >
                <span className="max-w-[120px] truncate">{storageId}</span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onFileRemove(storageId)}
                    className="text-muted-foreground hover:text-foreground ml-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const labelLower = field.label.toLowerCase();
  const isEmailField = labelLower.includes('email');
  const isPhoneField = labelLower.includes('phone');

  const emailValid = !isEmailField || !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const phoneValid = !isPhoneField || !value || /^\+\d[\d\s\-()]{6,}$/.test(value);
  const hasError = (isEmailField && !emailValid) || (isPhoneField && !phoneValid);

  let inputType = 'text';
  if (field.fieldType === 'number') inputType = 'number';
  else if (field.fieldType === 'date') inputType = 'date';
  else if (isEmailField) inputType = 'email';
  else if (isPhoneField) inputType = 'tel';

  return (
    <div className="flex flex-col gap-1">
      <Input
        type={inputType}
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        placeholder={
          isPhoneField ? '+1 234 567 8900' : (field.description ?? `Enter ${field.label}`)
        }
        className={cn('!h-10', hasError && 'border-destructive focus-visible:ring-destructive')}
      />
      {isEmailField && !emailValid && (
        <p className="text-xs text-destructive">Please enter a valid email address</p>
      )}
      {isPhoneField && !phoneValid && (
        <p className="text-xs text-destructive">
          Please enter a full phone number with country code (e.g. +1 234 567 8900)
        </p>
      )}
    </div>
  );
}
